import type { HandoffAssignment, OfferReadinessField, OfferRerunSettings, StoreOfferPolicy } from "@/api/types";

/** The backend's defaults (rogue/backend/src/modules/stores/store-policy.ts). */
export const DEFAULT_OFFER_POLICY: StoreOfferPolicy = {
  offers: { maxPercentOfValuation: 95, incrementCents: 10_000, autoApproveMaxCents: 0, valuationMaxAgeDays: 7 },
  negotiation: {
    requiredInfo: ["mileage", "vin"],
    openAtCeilingGapPercent: 20,
    askPriceGapPercent: 25,
    openingBelowCeilingCents: 50_000,
    maxStepCents: 50_000,
    handoffGapCents: 50_000,
  },
  handoff: { assignment: "ROUND_ROBIN" },
};

/** The backend's re-run defaults for offer integrations (modules/offer-api/offer-rerun-rules.ts). */
export const DEFAULT_RERUN_SETTINGS: OfferRerunSettings = {
  mileage: { enabled: true, increaseMiles: 500, decreaseMiles: 2000 },
  expiry: { enabled: true, useProviderExpiry: true, days: 12 },
};

/** Field errors for re-run settings, keyed like the backend's (`settings.mileage.increaseMiles`). */
export function rerunSettingsErrors(s: OfferRerunSettings): Record<string, string> {
  const errors: Record<string, string> = {};
  const whole = (v: number, min: number, max: number) => Number.isInteger(v) && v >= min && v <= max;
  if (!whole(s.mileage.increaseMiles, 1, 100_000)) errors["settings.mileage.increaseMiles"] = "1 to 100,000 miles";
  if (!whole(s.mileage.decreaseMiles, 1, 100_000)) errors["settings.mileage.decreaseMiles"] = "1 to 100,000 miles";
  if (!whole(s.expiry.days, 1, 365)) errors["settings.expiry.days"] = "1 to 365 days";
  return errors;
}

export const READINESS_LABEL: Record<OfferReadinessField, string> = {
  mileage: "Mileage (confirmed)",
  vin: "VIN",
  title_status: "Title status",
  condition: "Condition",
  photos: "Images",
};

export const ASSIGNMENT_LABEL: Record<HandoffAssignment, string> = {
  ROUND_ROBIN: "Round robin among buyers",
  STORE_MANAGER: "Store manager",
  NONE: "Nobody (stays unassigned)",
};

const dollars = (cents: number) => `$${(cents / 100).toLocaleString("en-US")}`;

/** Field errors for an offer policy, keyed like the backend's (e.g. `negotiation.askPriceGapPercent`). */
export function offerPolicyErrors(p: StoreOfferPolicy): Record<string, string> {
  const errors: Record<string, string> = {};
  const whole = (v: number) => Number.isInteger(v);
  const { offers: o, negotiation: n } = p;
  if (!(o.maxPercentOfValuation >= 1 && o.maxPercentOfValuation <= 150)) errors["offers.maxPercentOfValuation"] = "Between 1% and 150%";
  if (!(whole(o.incrementCents) && o.incrementCents > 0)) errors["offers.incrementCents"] = "Enter an amount above $0";
  if (!(whole(o.autoApproveMaxCents) && o.autoApproveMaxCents >= 0)) errors["offers.autoApproveMaxCents"] = "Enter $0 or more";
  if (!(whole(o.valuationMaxAgeDays) && o.valuationMaxAgeDays > 0)) errors["offers.valuationMaxAgeDays"] = "At least 1 day";
  for (const k of ["openAtCeilingGapPercent", "askPriceGapPercent"] as const)
    if (!(whole(n[k]) && n[k] >= 0 && n[k] <= 100)) errors[`negotiation.${k}`] = "A whole % between 0 and 100";
  if (!errors["negotiation.askPriceGapPercent"] && n.askPriceGapPercent < n.openAtCeilingGapPercent)
    errors["negotiation.askPriceGapPercent"] = "Must be at least the open-at-max gap";
  for (const k of ["openingBelowCeilingCents", "maxStepCents", "handoffGapCents"] as const)
    if (!(whole(n[k]) && n[k] >= 0)) errors[`negotiation.${k}`] = "Enter $0 or more";
  return errors;
}

/** Readable rules, as the backend lists them on the store. */
export function describeOfferPolicy(p: StoreOfferPolicy): Array<{ key: string; label: string; value: string }> {
  const n = p.negotiation;
  return [
    { key: "maxOffer", label: "Max offer (API valuations)", value: `${p.offers.maxPercentOfValuation}% of the valuation` },
    { key: "increment", label: "Offer increment", value: dollars(p.offers.incrementCents) },
    {
      key: "opening",
      label: "Opening offer",
      value: `At the max when the seller's price is more than ${n.openAtCeilingGapPercent}% above it, otherwise ${dollars(n.openingBelowCeilingCents)} below the max`,
    },
    {
      key: "askPrice",
      label: "Ask the seller's price first",
      value: `When their price is unknown or more than ${n.askPriceGapPercent}% above the max (asked once)`,
    },
    { key: "maxStep", label: "Raises", value: `Up to ${dollars(n.maxStepCents)} at a time, never past the max` },
    { key: "handoffGap", label: "Hand off on a gap of", value: `${dollars(n.handoffGapCents)} or less above the max` },
    {
      key: "requiredInfo",
      label: "Needed before an offer",
      value: [
        "year, make, model",
        ...n.requiredInfo.map((f) => ({ mileage: "confirmed mileage", vin: "VIN", photos: "images" })[f as string] ?? f.replace("_", " ")),
      ].join(", "),
    },
    { key: "assignment", label: "Handoff assignment", value: ASSIGNMENT_LABEL[p.handoff.assignment] },
    { key: "valuationMaxAge", label: "Valuation valid for", value: `${p.offers.valuationMaxAgeDays} days` },
  ];
}
